import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useTheme } from '../redux/hooks/useTheme';
import { useUserData } from '../redux/hooks/useUserData';

/**
 * UserProfileDropdown
 *
 * Displays the logged-in user's profile with avatar, name, and dropdown menu.
 * Shows user details and logout option.
 */
const UserProfileDropdown = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user, logout, accessibleOrganizations, accessibleHealthSystems } = useAuthContext();
  const { theme } = useTheme();
  const { currentUser } = useUserData();
  const isDark = theme.mode === 'dark';
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if no user is logged in
  if (!user) return null;

  // Handle logout - use centralized logout from AuthContext
  const handleLogout = async () => {
    await logout('/admin/login');
  };

  // Generate initials from user name - prioritize Redux currentUser
  const getInitials = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName[0]}${currentUser.lastName[0]}`.toUpperCase();
    } else if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    } else if (currentUser?.username || user.username) {
      return (currentUser?.username || user.username).substring(0, 2).toUpperCase();
    } else if (currentUser?.email || user.email) {
      return (currentUser?.email || user.email).substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Generate gradient based on user name/email
  const getGradient = () => {
    const seed = currentUser?.email || user.email || currentUser?.username || user.username || 'user';
    let hash = 0;
    for (let i = 0; i < seed.length; i++) { hash = (hash * 31 + seed.charCodeAt(i)) >>> 0; }
    const hue = hash % 360;
    return `linear-gradient(135deg, hsl(${hue}, 70%, 80%), hsl(${(hue + 60) % 360}, 70%, 60%))`;
  };

  // Get user display name - prioritize Redux currentUser
  const getDisplayName = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    } else if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else if (currentUser?.username || user.username) {
      return currentUser?.username || user.username;
    } else if (currentUser?.email || user.email) {
      return (currentUser?.email || user.email).split('@')[0];
    }
    return 'User';
  };

  // Get user role display
  const getRoleDisplay = () => {
    if (user.isAnkrAdmin) return 'Ankr Admin';
    if (user.role === 'admin') return 'Admin';
    if (user.role === 'sales') return 'Sales';
    return user.role || 'User';
  };

  // Format timestamp to readable date
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Get token type display
  const getTokenTypeDisplay = () => {
    if (!user.tokenType) return null;
    return user.tokenType.replace('core_server_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const initials = getInitials();
  const displayName = getDisplayName();
  const roleDisplay = getRoleDisplay();
  const showAllFields = user.isAnkrAdmin; // Show all fields for Ankr admins, even if empty

  return (
    <div className={`user-profile-dropdown ${className} ml-2`} ref={dropdownRef}>
      <button
        type="button"
        className="btn header-item bg-light-subtle border-start border-end d-flex align-items-center gap-2 px-3"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {/* Avatar */}
        <div
          className="d-flex align-items-center justify-content-center text-white fw-bold rounded-circle"
          style={{
            width: 40,
            height: 40,
            background: getGradient(),
            fontSize: 16,
            textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
            flexShrink: 0
          }}
        >
          {initials}
        </div>

        {/* User Name */}
        <div className="d-none d-sm-flex flex-column align-items-start">
          <span className="fw-semibold text-truncate" style={{ maxWidth: 150 }}>
            {displayName}
          </span>
          <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
            {roleDisplay}
          </span>
        </div>

        <i className={`fa fa-chevron-${open ? 'up' : 'down'} d-none d-xl-inline-block`}></i>
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className={`dropdown-menu dropdown-menu-end show ${isDark ? 'dropdown-menu-dark' : ''}`}
          style={{ minWidth: 250, maxWidth: 350, maxHeight: '50vh', overflowY: 'auto' }}
        >
          {/* User Info */}
          <div className="dropdown-item">
            <div className="fw-bold">{displayName}</div>
            <div className="text-muted small">{currentUser?.email || user.email || currentUser?.username || user.username || 'No email available'}</div>
          </div>

          <div className="dropdown-divider"></div>

          {/* Basic User Details - Always show for admins, conditionally for others */}
          {(showAllFields || user.id) && (
            <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-fingerprint me-2 text-muted"></i>
              <strong>ID:</strong> <span className="text-muted small">{user.id || 'N/A'}</span>
            </a>
          )}

          {(showAllFields || currentUser?.username || user.username) && (
            <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-user me-2 text-muted"></i>
              <strong>Username:</strong> <span className="text-muted small">{currentUser?.username || user.username || 'N/A'}</span>
            </a>
          )}

          {(showAllFields || user.tokenType) && (
            <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-key me-2 text-muted"></i>
              <strong>Type:</strong> <span className="text-muted small">{getTokenTypeDisplay() || 'N/A'}</span>
            </a>
          )}

          {(showAllFields || roleDisplay) && (
            <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-user-shield me-2 text-muted"></i>
              <strong>Role:</strong> <span className="text-muted small">{roleDisplay || 'N/A'}</span>
            </a>
          )}

          {/* Accessible Organizations - Show for Sales Users and Ankr Admins */}
          {(user.isSalesUser || user.isAnkrAdmin) && (
            <>
              <div className="dropdown-divider"></div>
              <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
                <i className="fas fa-building me-2 text-muted"></i>
                <strong>Organizations</strong> <span className="text-muted small">({accessibleOrganizations?.length || 0})</span>
              </a>
              {accessibleOrganizations && accessibleOrganizations.length > 0 ? (
                <div className="px-3 py-1 small text-muted" style={{ maxHeight: 100, overflowY: 'auto' }}>
                  {accessibleOrganizations.map((org, idx) => (
                    <div key={org.id || idx} className="py-1">
                      • {org.name || `Org ${org.id}`}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-1 small text-muted">
                  No organizations assigned
                </div>
              )}
            </>
          )}

          {/* User's Team - Show for Sales Users and Ankr Admins 
          {(user.isSalesUser || user.isAnkrAdmin) && (
            <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-users me-2 text-muted"></i>
              <strong>Team:</strong>
              <div className="text-muted small mt-1">
                {user.team_name || 'N/A'}
                {user.team_id && <div className="text-muted" style={{ fontSize: '0.7rem' }}>ID: {user.team_id}</div>}
              </div>
            </a>
          )} */}

          {/* Accessible Healthcare Systems - Show for Healthcare Users and Ankr Admins */}
          {(user.isHealthcareUser || user.isAnkrAdmin) && (
            <>
              <div className="dropdown-divider"></div>
              <a className="dropdown-item" href="#" onClick={(e) => e.preventDefault()}>
                <i className="fas fa-hospital-alt me-2 text-muted"></i>
                <strong>Health Systems</strong> <span className="text-muted small">
                  ({accessibleHealthSystems?.length || user.health_systems?.length || 0})
                </span>
              </a>
              {/* Prefer accessibleHealthSystems from context over JWT health_systems */}
              {(accessibleHealthSystems && accessibleHealthSystems.length > 0) ? (
                <div className="px-3 py-1 small text-muted" style={{ maxHeight: 100, overflowY: 'auto' }}>
                  {accessibleHealthSystems.map((hs, idx) => (
                    <div key={hs.id || idx} className="py-1">
                      • {hs.name || `System ${hs.id}`}
                    </div>
                  ))}
                </div>
              ) : user.health_systems && user.health_systems.length > 0 ? (
                <div className="px-3 py-1 small text-muted" style={{ maxHeight: 100, overflowY: 'auto' }}>
                  {user.health_systems.map((hs, idx) => (
                    <div key={hs.id || idx} className="py-1">
                      • {hs.name || `System ${hs.id}`}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-1 small text-muted">
                  No healthcare systems assigned
                </div>
              )}
            </>
          )}

          {/* Token Metadata - Admin only */}
          {showAllFields && (
            <>
              <div className="dropdown-divider"></div>
              <div className="dropdown-header text-muted small">Token Metadata</div>

              {user.iat && (
                <a className="dropdown-item small" href="#" onClick={(e) => e.preventDefault()}>
                  <i className="fas fa-clock me-2 text-muted"></i>
                  <strong>Issued:</strong> <span className="text-muted">{formatTimestamp(user.iat)}</span>
                </a>
              )}

              {user.exp && (
                <a className="dropdown-item small" href="#" onClick={(e) => e.preventDefault()}>
                  <i className="fas fa-stopwatch me-2 text-muted"></i>
                  <strong>Expires:</strong> <span className="text-muted">{formatTimestamp(user.exp)}</span>
                </a>
              )}

              {user.aud && (
                <a className="dropdown-item small" href="#" onClick={(e) => e.preventDefault()}>
                  <i className="fas fa-sitemap me-2 text-muted"></i>
                  <strong>Audience (Orgs):</strong> <span className="text-muted" style={{ display: 'block', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{Array.isArray(user.aud) ? user.aud.join(', ') : user.aud}</span>
                </a>
              )}
            </>
          )}

          <div className="dropdown-divider"></div>

          {/* Profile Page Link */}
          <button className="dropdown-item" onClick={() => { setOpen(false); navigate('/admin/profile'); }}>
            <i className="fas fa-user-circle me-2"></i>
            View Full Profile
          </button>

          {/* Logout */}
          <button className="dropdown-item text-danger" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt me-2"></i>
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
